import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { APP_THEME } from '../theme';

const getAuthErrorMessage = (error: unknown): string => {
  const code = (error as { code?: string })?.code;

  switch (code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    default:
      return 'Something went wrong. Please try again.';
  }
};

const AuthDialog: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const {
    authDialogOpen,
    authDialogMode,
    closeAuthDialog,
    openAuthDialog,
    signIn,
    signUp,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSignup = authDialogMode === 'signup';

  useEffect(() => {
    if (!authDialogOpen) {
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setError(null);
      setSubmitting(false);
    }
  }, [authDialogOpen, authDialogMode]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (isSignup && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      if (isSignup) {
        await signUp(email.trim(), password);
      } else {
        await signIn(email.trim(), password);
      }

      closeAuthDialog();
      navigate('/dashboard');
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={authDialogOpen}
      onClose={closeAuthDialog}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
          bgcolor: APP_THEME.colors.background,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: 'white',
          color: APP_THEME.colors.text,
          px: { xs: 2.5, sm: 3 },
          py: { xs: 3, sm: 3.5 },
          position: 'relative',
          borderBottom: `1px solid ${APP_THEME.colors.text}14`,
        }}
      >
        <IconButton
          onClick={closeAuthDialog}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: APP_THEME.colors.text,
          }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isSignup ? (
            <PersonAddAltIcon sx={{ color: APP_THEME.colors.primary }} />
          ) : (
            <LoginIcon sx={{ color: APP_THEME.colors.primary }} />
          )}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, color: APP_THEME.colors.text }}>
              {isSignup ? 'Create your account' : 'Welcome back'}
            </Typography>
            <Typography sx={{ opacity: 0.7, mt: 0.5, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {isSignup
                ? 'Sign up to save walking tours for later'
                : 'Log in to access your saved tours'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 3, sm: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 3,
            p: 0.5,
            bgcolor: `${APP_THEME.colors.text}08`,
            borderRadius: 2,
          }}
        >
          <Button
            fullWidth
            onClick={() => openAuthDialog('login')}
            variant={!isSignup ? 'contained' : 'text'}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              bgcolor: !isSignup ? 'white' : 'transparent',
              color: APP_THEME.colors.text,
              boxShadow: !isSignup ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              '&:hover': {
                bgcolor: !isSignup ? 'white' : `${APP_THEME.colors.text}08`,
                boxShadow: !isSignup ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              },
            }}
          >
            Log In
          </Button>
          <Button
            fullWidth
            onClick={() => openAuthDialog('signup')}
            variant={isSignup ? 'contained' : 'text'}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              bgcolor: isSignup ? 'white' : 'transparent',
              color: APP_THEME.colors.text,
              boxShadow: isSignup ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              '&:hover': {
                bgcolor: isSignup ? 'white' : `${APP_THEME.colors.text}08`,
                boxShadow: isSignup ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              },
            }}
          >
            Sign Up
          </Button>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{ mb: isSignup ? 2 : 3 }}
          />
          {isSignup && (
            <TextField
              fullWidth
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting}
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              bgcolor: APP_THEME.colors.primary,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#ff5252',
                boxShadow: `0 4px 12px ${APP_THEME.colors.primary}44`,
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : isSignup ? (
              'Create Account'
            ) : (
              'Log In'
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
